import React, { useState, useEffect, useCallback } from 'react';
import { RefreshCw, AlertCircle, Calendar, Download } from 'lucide-react';
import api from '../config/api';
import { useLanguage } from '../context/LanguageContext';
import { downloadCsv, buildReportRows } from '../utils/exportCsv';
import { showNotification } from '../components/GlobalNotification';

function Reports() {
  const { t } = useLanguage();
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedPeriod, setSelectedPeriod] = useState('daily');
  const [selectedDate, setSelectedDate] = useState(new Date().toLocaleDateString('en-CA', {timeZone: 'Asia/Katmandu'}));

  const fetchReportData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Each report takes different query params - daily wants a date, weekly a
      // week start, monthly a year/month pair. Sending the wrong one silently
      // falls back to "now" on the server.
      let endpoint = `/api/reports/${selectedPeriod}`;
      if (selectedDate) {
        if (selectedPeriod === 'daily') {
          endpoint += `?date=${selectedDate}`;
        } else if (selectedPeriod === 'weekly') {
          endpoint += `?startDate=${selectedDate}`;
        } else if (selectedPeriod === 'monthly') {
          // The month input gives us "YYYY-MM"
          const [year, month] = selectedDate.split('-');
          endpoint += `?year=${year}&month=${month}`;
        }
      }

      const response = await api.get(endpoint);
      setReportData(response.data);
    } catch (error) {
      setError(t('Failed to fetch the {period} report', { period: selectedPeriod }));
      console.error('Report fetch error:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedPeriod, selectedDate, t]);

  useEffect(() => {
    fetchReportData();
  }, [fetchReportData]);

  const handlePeriodChange = (period) => {
    setSelectedPeriod(period);

    // The monthly tab uses a month input ("YYYY-MM") and the others use a date
    // input ("YYYY-MM-DD"). Without reshaping the value the input renders blank.
    setSelectedDate((current) => {
      if (!current) {
        return current;
      }

      const [year, month, day] = current.split('-');

      if (period === 'monthly') {
        return `${year}-${month}`;
      }

      return day ? current : `${year}-${month}-01`;
    });
  };

  const formatCurrency = (amount) => {
    const value = amount || 0;
    return `Rs. ${value.toFixed(2)}`;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString();
  };

  const getPeriodLabel = () => {
    switch (selectedPeriod) {
      case 'daily':
        return `Daily Report - ${formatDate(selectedDate)}`;
      case 'weekly':
        return `Weekly Report - Week of ${formatDate(selectedDate)}`;
      case 'monthly':
        return `Monthly Report - ${new Date(selectedDate).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}`;
      default:
        return 'Report';
    }
  };

  const handleExport = () => {
    if (!reportData) {
      return;
    }

    const label = getPeriodLabel();
    const safeLabel = label.replace(/[^a-z0-9]+/gi, '-').toLowerCase();

    downloadCsv(`${safeLabel}.csv`, buildReportRows(reportData, label));
    showNotification('success', t('Report downloaded.'));
  };

  const getDateInputType = () => {
    switch (selectedPeriod) {
      case 'daily':
        return 'date';
      case 'weekly':
        return 'date';
      case 'monthly':
        return 'month';
      default:
        return 'date';
    }
  };

  return (
    <div>
      {/* Header - the period tabs carry the title on a phone, so the heading
          only takes up room once the screen is wide enough for it */}
      <div className="flex flex-col lg:flex-row justify-between items-stretch lg:items-center mb-6 md:mb-8 gap-4">
        {/* The heading waits for lg: at tablet widths the controls alone need
            most of the row, and squeezing the title in overflowed the page */}
        <div className="hidden lg:block">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">{t('Reports')}</h1>
          <p className="text-gray-600">{t('Business insights and analytics')}</p>
        </div>

        {/* flex-wrap from sm so a tablet lays the controls out in one or two
            rows on its own rather than clipping them */}
        <div className="flex flex-col sm:flex-row sm:flex-wrap sm:justify-end sm:items-center gap-2 sm:gap-3">
          {/* Period Selection - full-width equal tabs on mobile */}
          <div className="flex bg-gray-100 rounded-lg p-1">
            {['daily', 'weekly', 'monthly'].map((period) => (
              <button
                key={period}
                onClick={() => handlePeriodChange(period)}
                className={`flex-1 sm:flex-none px-3 sm:px-4 py-2 min-h-[40px] rounded-md text-sm font-medium whitespace-nowrap transition-colors ${
                  selectedPeriod === period
                    ? 'bg-white text-cafe-600 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                {period.charAt(0).toUpperCase() + period.slice(1)}
              </button>
            ))}
          </div>

          {/* Date Selection */}
          <input
            type={getDateInputType()}
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            className="input-field min-h-[44px] sm:w-40"
          />

          {/* Export and Refresh share one row on mobile rather than stacking */}
          <div className="flex gap-2 sm:contents">
            <button
              onClick={handleExport}
              className="btn-secondary flex-1 sm:flex-none flex items-center justify-center min-h-[44px] whitespace-nowrap"
              disabled={loading || !reportData}
              title={t('Download this report as a CSV spreadsheet')}
            >
              <Download className="h-4 w-4 mr-2 flex-shrink-0" />
              {t('Export CSV')}
            </button>

            <button
              onClick={fetchReportData}
              className="btn-secondary flex-1 sm:flex-none flex items-center justify-center min-h-[44px] whitespace-nowrap"
              disabled={loading}
            >
              <RefreshCw className={`h-4 w-4 mr-2 flex-shrink-0 ${loading ? 'animate-spin' : ''}`} />
              {t('Refresh')}
            </button>
          </div>
        </div>
      </div>

      {/* Report Content */}
      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cafe-600 mx-auto mb-4"></div>
          <p className="text-gray-600">{t('Loading report data...')}</p>
        </div>
      ) : error ? (
        <div className="text-center py-12">
          <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <p className="text-red-600 mb-4">{error}</p>
          <button onClick={fetchReportData} className="btn-primary">
            {t('Try Again')}
          </button>
        </div>
      ) : reportData ? (
        <div className="space-y-4 sm:space-y-6">
          {/* Period Title */}
          <div className="text-center">
            <h2 className="text-lg sm:text-2xl font-bold text-gray-900 break-words">{getPeriodLabel()}</h2>
          </div>

          {/* Two up from md so a tablet is not one long column of half-empty
              cards. Each of these is a short list, so they pair well. */}
          <div className="grid gap-4 sm:gap-6 md:grid-cols-2">
          {/* Payment Breakdown - Moved to Top */}
          <div className="card p-4 sm:p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('Payment Breakdown')}</h3>
            <div className="space-y-4">
              <div className="flex justify-between items-center gap-3">
                <span className="text-gray-600">{t('Cash Payments')}</span>
                <span className="font-semibold text-green-600 whitespace-nowrap">
                  {formatCurrency(reportData.cashRevenue)}
                </span>
              </div>
              <div className="flex justify-between items-center gap-3">
                <span className="text-gray-600">{t('Bank Payments')}</span>
                <span className="font-semibold text-blue-600 whitespace-nowrap">
                  {formatCurrency(reportData.bankRevenue)}
                </span>
              </div>
              {/* Part of the revenue above, but not collected yet - shown so a
                  strong day on paper isn't mistaken for a strong day in cash */}
              <div className="flex justify-between items-center gap-3">
                <span className="text-gray-600">{t('Sold on Due')}</span>
                <span className="font-semibold text-amber-600 whitespace-nowrap">
                  {formatCurrency(reportData.dueRevenue || 0)}
                </span>
              </div>
              <div className="flex justify-between items-center gap-3">
                <span className="text-gray-600">{t('Total Discounts')}</span>
                <span className="font-semibold text-orange-600 whitespace-nowrap">
                  {formatCurrency(reportData.totalDiscounts || 0)}
                </span>
              </div>
              <div className="border-t pt-2">
                <div className="flex justify-between items-center gap-3 font-semibold">
                  <span className="text-gray-900">{t('Total Revenue')}</span>
                  <span className="text-gray-900 whitespace-nowrap">
                    {formatCurrency(reportData.totalRevenue)}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Expenses Section */}
          <div className="card p-4 sm:p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('Expenses')}</h3>
            <div className="space-y-4">
              <div className="flex justify-between items-center gap-3">
                <span className="text-gray-600">{t('Total Expenses')}</span>
                <span className="font-semibold text-red-600 whitespace-nowrap">
                  {formatCurrency(reportData.totalExpenses)}
                </span>
              </div>
            </div>
          </div>

          {/* Profit/Loss Section */}
          <div className="card p-4 sm:p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('Profit & Loss')}</h3>
            <div className="space-y-4">
              <div className="flex justify-between items-center gap-3">
                <span className="text-gray-600">{t('Total Revenue')}</span>
                <span className="font-semibold text-green-600 whitespace-nowrap">
                  {formatCurrency(reportData.totalRevenue)}
                </span>
              </div>
              <div className="flex justify-between items-center gap-3">
                <span className="text-gray-600">{t('Total Expenses')}</span>
                <span className="font-semibold text-red-600 whitespace-nowrap">
                  {formatCurrency(reportData.totalExpenses)}
                </span>
              </div>
              <div className="border-t pt-2">
                <div className="flex justify-between items-center gap-3 font-semibold">
                  <span className="text-gray-900">{t('Net Profit/Loss')}</span>
                  <span className={`font-bold whitespace-nowrap ${
                    (reportData.netProfit || 0) >= 0
                      ? 'text-green-600'
                      : 'text-red-600'
                  }`}>
                    {formatCurrency(reportData.netProfit)}
                  </span>
                </div>
              </div>
              {reportData.netProfit < 0 && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 mt-2">
                  <p className="text-red-800 text-sm">
                    ⚠️ Loss Alert: Expenses exceed revenue by {formatCurrency(Math.abs(reportData.netProfit))}
                  </p>
                </div>
              )}
              {reportData.netProfit > 0 && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-3 mt-2">
                  <p className="text-green-800 text-sm">
                    ✅ Profit: Revenue exceeds expenses by {formatCurrency(reportData.netProfit)}
                  </p>
                </div>
              )}
            </div>
          </div>
          </div>

          {/* Items Sold List */}
          {reportData.itemsSold && reportData.itemsSold.length > 0 && (
            <div className="card p-4 sm:p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('Items Sold')}</h3>
              {/* Two columns of rows once there is width for them, rather than
                  one column of very wide, mostly empty rows */}
              <div className="grid gap-3 lg:grid-cols-2">
                {[...reportData.itemsSold]
                  .sort((a, b) => b.quantity - a.quantity)
                  .map((item, index) => (
                    <div key={item.itemName} className="flex items-center justify-between gap-3 p-3 bg-gray-50 rounded-lg">
                      {/* min-w-0 lets the name truncate instead of pushing the
                          revenue column off a narrow screen */}
                      <div className="flex items-center min-w-0 flex-1">
                        <div className="w-8 h-8 bg-cafe-100 rounded-full flex items-center justify-center mr-3 flex-shrink-0">
                          <span className="text-sm font-semibold text-cafe-600">
                            {index + 1}
                          </span>
                        </div>
                        <div className="min-w-0">
                          <h4 className="font-medium text-gray-900 truncate">{item.itemName}</h4>
                          <p className="text-xs sm:text-sm text-gray-500 truncate">
                            Qty: {item.quantity} • {formatCurrency(item.price)} each
                          </p>
                        </div>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <p className="font-semibold text-gray-900 whitespace-nowrap">
                          {formatCurrency(item.totalRevenue)}
                        </p>
                        {/* The label is only worth its width on a wide screen */}
                        <p className="hidden sm:block text-sm text-gray-500">{t('Final Revenue')}</p>
                        {item.totalDiscount > 0 && (
                          <p className="text-xs text-orange-600 whitespace-nowrap">
                            Discount: -{formatCurrency(item.totalDiscount)}
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          )}

          {/* No Data Message */}
          {reportData.totalOrders === 0 && (
            <div className="text-center py-12">
              <Calendar className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">{t('No Data Available')}</h3>
              <p className="text-gray-500">
                No orders or expenses found for the selected {selectedPeriod} period.
              </p>
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
}

export default Reports;